/**
 * EmailListRow — renders a single email row from the JSON API.
 * Props: email (canonical Thread from /api/email/v1/threads), store, isActive (bool), i18n
 */

import { h, Fragment } from 'preact'
import { sanitizePlainSnippet } from '@tshmanager/email-shared'
import { PersonAvatar } from './PersonAvatar.jsx'
import { tagTextColor, timeAgo, threadFromDisplayName } from '../../modules/email/email-helpers.js'

export function EmailListRow({ email, store, isActive, i18n }) {
  const isUnread  = email.read === false
  const isStarred = !!email.starred
  const fromName  = threadFromDisplayName(email)

  const mailboxInfo = store.mailboxes.value.find(m => m.id === email.mailbox_id) || null

  // Left bar color: always driven by the mailbox color
  const mailboxBarColor = mailboxInfo?.avatar_color ? `#${mailboxInfo.avatar_color}` : null

  function handleClick(e) {
    e.preventDefault()
    store.selectThread(email.id)
  }

  function handleDblClick(e) {
    e.preventDefault()
    store.openThreadWindow(email.id)
  }

  function handleCheckboxChange(e) {
    e.stopPropagation()
    store.toggleSelected(email.id)
  }

  async function handleStarClick(e) {
    e.stopPropagation()
    e.preventDefault()
    await store.updateEmailState(email.id, 'star')
  }

  const threadSize = email.thread_size || ((email.thread_count || 0) + 1)
  const tags = email.tags || []
  const readBy = email.read_by || []
  const isSelected = store.selectedIds.value.includes(email.id)
  const hasAttachments = !!(email.has_attachments || email.files_attached)
  const activityAt = email.last_activity_at || email.last_activity || email.sent_at

  return (
    <div
      id={`email_${email.id}_row`}
      class={[
        'email-list-row',
        isUnread    ? 'email-unread'              : '',
        isStarred   ? 'email-starred'             : '',
        isActive    ? 'email-list-row--active'    : '',
        isSelected  ? 'email-list-row--selected'  : '',
      ].filter(Boolean).join(' ')}
      data-email-id={email.id}
      data-status={email.status}
      data-draggable-email-id={email.id}
      draggable="true"
      style={mailboxBarColor ? { '--mailbox-bar-color': mailboxBarColor } : undefined}
      onMouseEnter={() => {
        if (!email.is_draft) store.scheduleThreadPrefetch(email.id)
      }}
      onMouseLeave={() => store.cancelThreadPrefetch()}
    >
      {/* Full-area click overlay */}
      <button
        type="button"
        class="email-row-click-overlay"
        aria-label={email.subject || i18n?.t('emails.no_subject')}
        onClick={handleClick}
        onDblClick={handleDblClick}
      />

      {/* Avatar + checkbox */}
      <div class="email-row-avatar-wrap flex-shrink-0" onClick={e => e.stopPropagation()}>
        <div class="email-row-avatar-bg position-relative">
          {email.sender_avatar_url || email.sender_person_name || fromName
            ? (
              <Fragment>
                <PersonAvatar
                  avatarUrl={email.sender_avatar_url || email.mailbox_avatar_url}
                  name={email.sender_person_name || fromName}
                  size={36}
                  cssClass="email-row-avatar"
                />
                <span
                  class={`email-read-badge position-absolute ${isUnread ? 'email-read-badge--unread' : 'email-read-badge--read'}`}
                  title={isUnread ? (i18n?.t('emails.unread') || 'Unread') : (i18n?.t('emails.read') || 'Read')}
                >
                  <i class={`bi ${isUnread ? 'bi-envelope-fill' : 'bi-envelope-open'}`} />
                </span>
              </Fragment>
            )
            : (
              <div
                class={`email-row-avatar email-row-avatar-placeholder d-flex align-items-center justify-content-center rounded-circle ${isUnread ? 'bg-primary-subtle' : 'bg-secondary-subtle'}`}
                style="width:36px;height:36px"
                title={isUnread ? (i18n?.t('emails.unread') || 'Unread') : (i18n?.t('emails.read') || 'Read')}
              >
                <i class={`bi ${isUnread ? 'bi-envelope-fill text-primary' : 'bi-envelope-open text-secondary'}`} style="font-size:0.85rem" />
              </div>
            )
          }
        </div>
        <div class="email-row-checkbox-bg">
          <input
            type="checkbox"
            class="form-check-input"
            value={email.id}
            checked={isSelected}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>

      {/* Main content */}
      <div class="email-row-body flex-grow-1 min-w-0">

        {/* Row 1: Sender + mailbox badge (all-mailboxes view) + date */}
        <div class="email-row-header d-flex align-items-baseline gap-1 mb-0">
          <span class="email-row-sender-name text-truncate">{fromName}</span>
          {threadSize > 1 && (
            <span class="email-row-thread-count text-muted fw-normal flex-shrink-0">
              ({threadSize})
            </span>
          )}
          <span class="email-row-date flex-shrink-0 text-muted ms-auto">
            {timeAgo(activityAt)}
          </span>
          {email.direction === 'outbound' && (
            <i class="bi bi-arrow-up-short text-primary flex-shrink-0" style="font-size:0.9rem" />
          )}
          {hasAttachments && (
            <i class="bi bi-paperclip text-muted flex-shrink-0" style="font-size:0.8rem" />
          )}
        </div>

        {/* Row 2: Subject */}
        <div class="email-row-subject d-flex align-items-center gap-1">
          <span class="text-truncate">
            {email.subject || i18n?.t('emails.no_subject')}
          </span>
        </div>

        {/* Row 3: Preview */}
        <div class="email-row-footer d-flex align-items-center gap-1">
          <span class="email-row-snippet text-muted text-truncate flex-grow-1">
            {sanitizePlainSnippet(email.preview || email.snippet, 120)}
          </span>
        </div>

        {/* Row 4: Tags (left) + read-by avatars + star (right) */}
        <div class="email-row-tags-row d-flex align-items-center gap-1 mt-1">
          <div class="email-row-tags d-flex flex-wrap gap-1 flex-grow-1" id={`email_tags_${email.id}`}>
            {tags.slice(0, 5).map(tag => (
              <span
                key={tag.id}
                class="badge email-tag-badge rounded-pill"
                style={tag.color ? { backgroundColor: tag.color, color: tagTextColor(tag.color) } : undefined}
              >
                {tag.tag}
              </span>
            ))}
            {tags.length > 5 && (
              <span class="badge bg-secondary-subtle text-secondary rounded-pill">
                +{tags.length - 5}
              </span>
            )}
          </div>

          {/* Read-by avatars */}
          <div id={`email_${email.id}_readers`} class="read-by-avatars d-flex align-items-center flex-shrink-0">
            {readBy.map((reader, i) => (
              <div key={i} class="read-avatar" style={{ '--avatar-z-index': 10 - i }} title={reader.person_name}>
                <PersonAvatar
                  avatarUrl={reader.person_avatar_url}
                  name={reader.person_name}
                  size={18}
                  cssClass="border border-white"
                />
              </div>
            ))}
          </div>

          {/* Star button — drafts are not Email threads */}
          {!email.is_draft && (
            <button
              class="email-row-star btn btn-link p-0"
              title={i18n?.t('emails.star')}
              onClick={handleStarClick}
            >
              <i
                class={`bi ${isStarred ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`}
                style="font-size:0.8rem"
              />
            </button>
          )}
        </div>

      </div>
    </div>
  )
}
